import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="discount-assignment"
export default class extends Controller {
  static targets = [
    "selectAll", "checkbox", "counter", "submitButton",
    "installmentCheckbox", "installmentSelectAll", "installmentInputs",
    "discountTotal", "netTotal",
    "itemDiscount", "itemNet",
    "studentDiscountPreview", "studentNetPreview",
  ];
  static values = { selectedText: { type: String, default: "selected" } };

  toggleAll() {
    const checked = this.selectAllTarget.checked;
    this.checkboxTargets.forEach((cb) => (cb.checked = checked));
    this.updateCounter();
  }

  toggleAllInstallments() {
    const checked = this.installmentSelectAllTarget.checked;
    this.installmentCheckboxTargets.forEach((cb) => (cb.checked = checked));
    this.updateInstallmentTotals();
  }

  updateInstallmentTotals() {
    let totalDiscount = 0;
    let totalNet = 0;

    this.installmentCheckboxTargets.forEach((cb, index) => {
      const amount = parseFloat(cb.dataset.installmentAmount) || 0;
      const discount = parseFloat(cb.dataset.installmentDiscount) || 0;
      const net = parseFloat(cb.dataset.installmentNet) || 0;

      // Update per-row discount/net display
      const discountCell = this.itemDiscountTargets[index];
      const netCell = this.itemNetTargets[index];

      if (cb.checked) {
        totalDiscount += discount;
        totalNet += net;
        if (discountCell) this.#setSpan(discountCell, "text-cta font-medium", `-${this.formatCurrency(discount)} F CFA`);
        if (netCell) netCell.textContent = `${this.formatCurrency(net)} F CFA`;
      } else {
        totalNet += amount; // Full amount when not discounted
        if (discountCell) this.#setSpan(discountCell, "text-gray-400", "-");
        if (netCell) netCell.textContent = `${this.formatCurrency(amount)} F CFA`;
      }
    });

    // Update footer totals
    if (this.hasDiscountTotalTarget) {
      this.#setSpan(this.discountTotalTarget, "text-cta font-medium", `-${this.formatCurrency(totalDiscount)} F CFA`);
    }
    if (this.hasNetTotalTarget) {
      this.netTotalTarget.textContent = `${this.formatCurrency(totalNet)} F CFA`;
    }

    // Update student row previews. A student already billed carries his own
    // items (a tranche already paid takes no discount): the row is computed
    // from them, not from the fee's theoretical totals.
    const excludedIds = this.installmentCheckboxTargets
      .filter((cb) => !cb.checked)
      .map((cb) => String(cb.dataset.installmentId));
    this.studentDiscountPreviewTargets.forEach((el) => {
      const discount = this.#ownTotals(el, excludedIds)?.discount ?? totalDiscount;
      if (discount > 0) {
        this.#setSpan(el, "text-cta font-medium", `-${this.formatCurrency(discount)} F CFA`);
      } else {
        this.#setSpan(el, "text-gray-400", "-");
      }
    });
    this.studentNetPreviewTargets.forEach((el) => {
      const net = this.#ownTotals(el, excludedIds)?.net ?? totalNet;
      el.textContent = `${this.formatCurrency(net)} F CFA`;
    });

    // Sync hidden inputs in the form
    this.syncInstallmentHiddenInputs();

    this.#syncSubmitState();

    // Update select-all indeterminate state
    if (this.hasInstallmentSelectAllTarget) {
      const total = this.installmentCheckboxTargets.length;
      const checked = this.installmentCheckboxTargets.filter((cb) => cb.checked).length;
      this.installmentSelectAllTarget.checked = checked === total;
      this.installmentSelectAllTarget.indeterminate = checked > 0 && checked < total;
    }
  }

  syncInstallmentHiddenInputs() {
    if (!this.hasInstallmentInputsTarget) return;

    const container = this.installmentInputsTarget;
    container.replaceChildren();

    // Add hidden inputs only for checked installments
    this.installmentCheckboxTargets.forEach((cb) => {
      if (cb.checked) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "included_installment_ids[]";
        input.value = cb.dataset.installmentId;
        container.appendChild(input);
      }
    });
  }

  updateCounter() {
    const count = this.checkboxTargets.filter((cb) => cb.checked).length;

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${count} ${this.selectedTextValue}`;
    }

    // Update select all state
    if (this.hasSelectAllTarget) {
      const total = this.checkboxTargets.length;
      this.selectAllTarget.checked = count === total && total > 0;
      this.selectAllTarget.indeterminate = count > 0 && count < total;
    }

    this.#syncSubmitState();
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(amount);
  }

  // Enable submit only when ≥1 student checked AND (no installment checkboxes OR ≥1 checked)
  #syncSubmitState() {
    if (!this.hasSubmitButtonTarget) return;

    const hasStudents = this.checkboxTargets.some((cb) => cb.checked);
    const hasInstallments = this.installmentCheckboxTargets.length === 0 ||
      this.installmentCheckboxTargets.some((cb) => cb.checked);
    const enabled = hasStudents && hasInstallments;

    this.submitButtonTarget.disabled = !enabled;
    if (enabled) {
      this.submitButtonTarget.classList.remove("btn-disabled", "bg-gray-300", "text-gray-500", "cursor-not-allowed");
      this.submitButtonTarget.classList.add("btn-primary");
    } else {
      this.submitButtonTarget.classList.add("btn-disabled", "bg-gray-300", "text-gray-500", "cursor-not-allowed");
      this.submitButtonTarget.classList.remove("btn-primary");
    }
  }

  // Build a <span> with given classes and text, replacing all children of parent
  // { discount, net } of a billed student's row, null when the row has no
  // items of its own. Each item is [gross, discount if included, if excluded].
  #ownTotals(cell, excludedIds) {
    const raw = cell.closest("tr")?.dataset.discountItems;
    if (!raw) return null;

    let gross = 0;
    let discount = 0;
    Object.entries(JSON.parse(raw)).forEach(([itemId, [itemGross, granted, kept]]) => {
      gross += itemGross;
      discount += excludedIds.includes(itemId) ? kept : granted;
    });
    return { discount, net: gross - discount };
  }

  #setSpan(parent, className, text) {
    const span = document.createElement("span");
    span.className = className;
    span.textContent = text;
    parent.replaceChildren(span);
  }
}
